import { deleteCommentThunk, getAllCommentsThunk } from '@redux/features/comment.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import React, { FC, useEffect, useState } from 'react';
import AddCommentModal from './AddCommentModal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import UpdateCommentModal from './UpdateCommentModal';
import ViewCommentModal from './ViewCommentModal';

const Comments: FC = () => {
    const [page, setPage] = useState<string>(`0`);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showAddCommentModal, setShowAddCommentModal] = useState<boolean>(false);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [showViewModal, setShowViewModal] = useState<boolean>(false);
    const [commentId, setCommentId] = useState<number>();
    const [selectedComment, setSelectedComment] = useState<any>(null);

    // -------------------------------------------------------------
    const dispatch = useAppDispatch();
    // -------------------------------------------------------------
    const { comments, loading } = useAppSelector((state: RootState) => state.commentState);
    // -------------------------------------------------------------
    const changePage = (page: string) => {
        setPage(page);
    };

    // -------------------------------------------------------------
    useEffect(() => {
        dispatch(getAllCommentsThunk());
    }, [page, dispatch]);
    // -----------------------------------------------------

    const handleConfirmDelete = async () => {
        if (commentId) {
            dispatch(deleteCommentThunk(commentId)).then((response: any) => {
                if (response.meta.requestStatus === 'fulfilled') {
                    setShowDeleteModal(false);
                    setCommentId(undefined);
                    dispatch(getAllCommentsThunk());
                }
            });
        }
    };

    const handleDeleteComment = (id: number) => {
        setCommentId(id);
        setShowDeleteModal(true);
    };

    const handleUpdateComment = (id: number) => {
        setCommentId(id);
        setShowUpdateModal(true);
    };

    const handleViewComment = (comment: any) => {
        setSelectedComment(comment);
        setShowViewModal(true);
    };

    // Helper function to truncate comment text
    const truncateComment = (text: string, maxLength: number = 100) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Helper function to check if comment is long
    const isLongComment = (text: string, threshold: number = 100) => {
        return text && text.length > threshold;
    };
    // -----------------------------------------------------

    // Helper function to get email from comment (handles both direct email and nested user.email)
    const getCommentEmail = (comment: any) => {
        return comment?.email || comment?.user?.email || '-';
    };

    // Helper function to get visibility status
    const getVisibilityStatus = (comment: any) => {
        return comment?.isVisible || comment?.visible || false;
    };

    return (
        <>
            <ViewCommentModal
                show={showViewModal}
                handleClose={() => {
                    setShowViewModal(false);
                    setSelectedComment(null);
                }}
                comment={selectedComment}
            />
            <UpdateCommentModal
                show={showUpdateModal}
                handleClose={() => {
                    setShowUpdateModal(false);
                    setCommentId(undefined);
                }}
                commentId={commentId || 0}
            />
            {/* <AddCommentModal handleClose={() => setShowAddCommentModal(false)} show={showAddCommentModal} /> */}
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => {
                    setShowDeleteModal(false);
                    setCommentId(undefined);
                }}
                onConfirm={handleConfirmDelete}
                title="Delete Comment"
                message="Are you sure you want to delete this comment? This action cannot be undone."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                id={commentId}
            />
            <BreadCrumb pageTitle={`Comments List`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Comments List</h3>
                        {/* <ButtonBase innerText="Add Comment" onClick={() => setShowAddCommentModal(true)} /> */}
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Email</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Comment</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Rate</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Visible</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Details</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={comments?.length ?? 0} animationKey={`comments-${page}`}>
                                            {comments?.map((comment: any) => (
                                                <AnimatedTableRow
                                                    rowKey={comment?.id}
                                                    detailRecord={comment}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{comment?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{getCommentEmail(comment) || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-col gap-1">
                                                            <span>{truncateComment(comment?.comment || '-')}</span>
                                                            {isLongComment(comment?.comment) && (
                                                                <button
                                                                    onClick={() => handleViewComment(comment)}
                                                                    className="text-xs text-blue-600 hover:text-blue-800 underline text-left"
                                                                >
                                                                    View Full Comment
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-3.5 py-2.5">{comment?.rate || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        {getVisibilityStatus(comment) ? (
                                                            <span className="text-green-600 font-semibold">Visible</span>
                                                        ) : (
                                                            <span className="text-red-600 font-semibold">Hidden</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-col justify-center items-center gap-2.5">
                                                            <button
                                                                className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                        rounded-md border border-blue-600
                                                        bg-white transition-all duration-300 ease-linear hover:scale-105"
                                                                onClick={() => {
                                                                    handleViewComment(comment);
                                                                }}
                                                            >
                                                                View Comment
                                                            </button>
                                                            <button
                                                                className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                        rounded-md border border-yellow-600
                                                        bg-white transition-all duration-300 ease-linear hover:scale-105"
                                                                onClick={() => {
                                                                    handleDeleteComment(comment?.id);
                                                                }}
                                                            >
                                                                delete comment
                                                            </button>
                                                            <button
                                                                className="mx-auto group flex w-full px-2 text-xs font-semibold py-1 items-center justify-center
                                                        rounded-md border border-yellow-600
                                                        bg-white transition-all duration-300 ease-linear hover:scale-105"
                                                                onClick={() => {
                                                                    handleUpdateComment(comment?.id);
                                                                }}
                                                            >
                                                                update status
                                                            </button>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                {/* <Pagination
                                    perPageData={comments?.totalElements}
                                    total={comments?.totalPages}
                                    currentPage={page as unknown as number}
                                    changePage={changePage}
                                    length={comments?.totalElements}
                                /> */}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default Comments;
